import type { BookingRecord, TripStatus, PaymentStatus } from '../../types';
import { API_BASE_URL, API_KEY, type ApiResponse } from '../../../api/auth/authApi';
import { DASHBOARD_API_BASE_URL } from '../config/config';
import { ADMIN_TOKEN_KEY } from '../../auth/adminAuth';

export type AdminBookingVehicle = {
  id: string;
  category: string;
  name: string;
  image: string;
  passengers: number;
  luggage: number;
  price: number;
  minimumBookingHours: number;
};

type AdminBookingVehicleApi = {
  id: number;
  category_name?: string;
  name?: string;
  image?: string;
  capacity?: number;
  max_suitcases?: number;
  base_price?: number;
  minimum_booking_hours?: number;
  minimumBookingHours?: number;
  is_active?: boolean;
};

type AdminBookingVehicleResponse =
  | AdminBookingVehicleApi[]
  | {
      results?: AdminBookingVehicleApi[];
    }
  | null;

type CreateBookingTripData = {
  serviceType: string;
  dayTripId: string;
  sourceNumber: string;
  sourceName: string;
  paymentStatus: 'pre_paid' | 'cash' | 'account_transfer' | 'paid';
  journeyType: string;
  pickupLocation: string;
  dropoffLocation: string;
  pickupDate: string;
  pickupTime: string;
  returnDate: string;
  returnTime: string;
  passengers: string;
  luggage: string;
  distanceKm?: number;
  durationHours?: number;
};

type CreateBookingCustomerData = {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  arrivalType: string;
  airline: string;
  flightNumber: string;
  arrivalTerminal: string;
  arrivalTime: string;
  locationValue?: string;
  options: string[];
  specialRequirements: string;
  bookingForSomeoneElse: boolean;
  otherPersonName: string;
  otherPersonPhone: string;
};

type CreateBookingPayload = {
  trip: CreateBookingTripData;
  customer: CreateBookingCustomerData;
  vehicles: Record<string, number>;
  vehiclePriceOverrides?: Record<string, number>;
  vehicle_price?: number;
  final_price?: number;
  estimated_price?: number;
};

export async function fetchAdminBookingVehicles(): Promise<AdminBookingVehicle[]> {
  try {
    const token = localStorage.getItem(ADMIN_TOKEN_KEY);

    const response = await fetch(
      `${DASHBOARD_API_BASE_URL}/api/admin/vehicles/`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...(API_KEY ? { 'X-API-Key': API_KEY } : {}),
        },
      }
    );

    const result = (await response
      .json()
      .catch(() => null)) as AdminBookingVehicleResponse;

    if (!response.ok) {
      return [];
    }

    const vehicles: AdminBookingVehicleApi[] = Array.isArray(result)
      ? result
      : result?.results ?? [];

    return vehicles
      .filter((vehicle) => vehicle.is_active === true)
      .map((vehicle) => ({
        id: String(vehicle.id),
        category: vehicle.category_name ?? '',
        name: vehicle.name ?? '',
        image: vehicle.image ?? '',
        passengers: Number(vehicle.capacity ?? 0),
        luggage: Number(vehicle.max_suitcases ?? 0),
        price: Number(vehicle.base_price ?? 0),
        minimumBookingHours: Number(
          vehicle.minimum_booking_hours ??
            vehicle.minimumBookingHours ??
            1
        ),
      }));
  } catch {
    return [];
  }
}

/**
 * Fetch all bookings.
 *
 * Vehicle IDs returned by the booking API are resolved to their
 * vehicle names using the admin vehicle API.
 */
export async function fetchBookings(): Promise<ApiResponse<BookingRecord[]>> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/admin/bookings/`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(API_KEY ? { 'X-API-Key': API_KEY } : {}),
      },
    });

    const result = await response.json().catch(() => null);

    if (!response.ok) {
      return {
        success: false,
        message: result?.message || 'Failed to fetch bookings',
      };
    }

    const bookings = Array.isArray(result?.data)
      ? result.data
      : Array.isArray(result)
        ? result
        : [];

    /*
     * Fetch vehicle information once and create a lookup map.
     *
     * Example:
     * vehicle ID 12 -> "Mercedes V-Class"
     */
    const vehicles = await fetchAdminBookingVehicles();

    const vehicleMap = new Map(
      vehicles.map((vehicle) => [
        String(vehicle.id),
        vehicle.name || vehicle.category || 'Standard',
      ])
    );

    return {
      success: true,
      message: 'Bookings fetched',
      data: bookings.map((item: any) => {
        const record = mapBookingApiItemToRecord(item);

        const vehicleId =
          typeof item.vehicle === 'object'
            ? item.vehicle?.id
            : item.vehicle;

        return {
          ...record,
          vehicle:
            vehicleMap.get(String(vehicleId)) ||
            (typeof item.vehicle === 'object'
              ? item.vehicle?.name ||
                item.vehicle?.category_name ||
                'Standard'
              : 'Standard'),
        };
      }),
    };
  } catch {
    return {
      success: false,
      message: 'Connection failed.',
    };
  }
}

/** Backend `Booking.STATUS_CHOICES`. */
const serviceTypeMap: Record<string, BookingRecord['service']> = {
  fleet: 'Transfers',
  hourly: 'Hourly Drivers',
  daytrips: 'Day Trips',
  private_tours: 'Day Trips',
  privatetours: 'Day Trips',
  ski_transfers: 'Transfers',
  skitransfers: 'Transfers',
};

const tripStatusMap: Record<string, TripStatus> = {
  pending: 'pending',
  confirmed: 'confirmed',
  in_progress: 'on-trip',
  completed: 'completed',
  incomplete: 'incomplete',
  cancelled: 'cancelled',
};

function parseBookingLocation(value: unknown): BookingRecord['location'] {
  if (!value) {
    return undefined;
  }

  if (typeof value === 'object') {
    return value as BookingRecord['location'];
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();

    if (!trimmed) {
      return undefined;
    }

    try {
      return JSON.parse(trimmed) as BookingRecord['location'];
    } catch {
      return trimmed;
    }
  }

  return undefined;
}

/** Maps a raw booking object from the Django admin API into a BookingRecord. */
export const mapBookingApiItemToRecord = (
  item: any,
  vehicleName?: string
): BookingRecord => {
  const vehicleId =
    typeof item.vehicle === 'object'
      ? item.vehicle?.id
      : item.vehicle;

  const resolvedVehicleName =
    vehicleName ||
    (typeof item.vehicle === 'object'
      ? item.vehicle?.name ||
        item.vehicle?.category_name
      : null);

  return {
    id: String(item.id),

    customer: {
      name: item.traveler_name || '',
      email: item.traveler_email || '',
    },

    reference: item.booking_id || '',

    service: serviceTypeMap[item.service_type] || 'Transfers',
    dayTripId:
      item.service_id != null
        ? String(item.service_id)
        : '',
    serviceImage: item.service_image || '',

    sourceNumber:
      item.sourceNumber ??
      item.source_number ??
      '',

    sourceName:
      item.sourceName ??
      item.source_name ??
      '',

    route: {
      from: item.pickup_location || '',
      to: item.drop_location || '',
    },

    location: parseBookingLocation(item.location),

    scheduledAt: item.pickup_datetime || '',

    /*
     * IMPORTANT:
     * `vehicle` is the display value used by the dashboard.
     * The booking API normally returns the vehicle ID, so use
     * the resolved vehicle name when available.
     */
    vehicle:
      resolvedVehicleName ||
      (vehicleId != null ? String(vehicleId) : 'Standard'),

    vehiclePrice: Number(item.vehicle_price ?? 0),

    driverAssigned: Boolean(
      item.driverAssigned ??
      item.driver_assigned ??
      item.driver ??
      item.driver_id ??
      item.assigned_driver
    ),

    driverId: String(
      item.driverId ??
      item.driver_id ??
      item.assigned_driver_id ??
      (typeof item.driver === 'object'
        ? item.driver?.id ?? item.driver?.driver_id
        : item.driver) ??
      (typeof item.assigned_driver === 'object'
        ? item.assigned_driver?.id ?? item.assigned_driver?.driver_id
        : item.assigned_driver) ??
      ''
    ),

    driverCode: String(
      item.driverCode ??
      item.driver_code ??
      (typeof item.driver === 'object'
        ? item.driver?.driver_id ?? item.driver?.code
        : '') ??
      (typeof item.assigned_driver === 'object'
        ? item.assigned_driver?.driver_id ?? item.assigned_driver?.code
        : '') ??
      ''
    ),

    driverName:
      item.driverName ||
      item.driver_name ||
      (typeof item.driver === 'object'
        ? item.driver?.driver_name || item.driver?.name
        : '') ||
      (typeof item.assigned_driver === 'object'
        ? item.assigned_driver?.driver_name ||
          item.assigned_driver?.name
        : '') ||
      '',

    driverAmount: Number(
      item.driverAmount ??
      item.driver_amount ??
      item.driver_fare ??
      0
    ),

    amountEur: Number(item.final_price ?? 0),

    payment:
      item.payment?.status === 'succeeded'
        ? 'paid'
        : 'unpaid',

    trip: tripStatusMap[item.status] || 'upcoming',

    passengers: Number(item.passengers ?? 0),

    luggage: Number(item.luggage ?? 0),

    customerPhone: item.traveler_phone || '',

    returnTrip: Boolean(item.return_trip),

    returnDateTime: item.return_datetime || null,

    paymentMethod: item.payment_method || '',

    depositAmount: Number(item.deposit_amount ?? 0),

    estimatedPrice: Number(item.estimated_price ?? 0),

    finalPrice: Number(item.final_price ?? 0),

    selectedOptions: Array.isArray(item.selected_options)
      ? item.selected_options
      : [],

    status: item.status || '',

    notes: item.notes || '',
  };
};

/**
 * Create a new booking.
 */
export async function createBooking(
  payload: CreateBookingPayload
): Promise<ApiResponse<BookingRecord>> {
  const selectedVehicles = Object.entries(payload.vehicles).filter(
    ([, quantity]) => Number(quantity) > 0
  );

  if (
    selectedVehicles.length !== 1 ||
    Number(selectedVehicles[0][1]) !== 1
  ) {
    return {
      success: false,
      message: 'Select exactly one vehicle for this booking.',
    };
  }

  const [vehicleId] = selectedVehicles[0];

  const bookingVehiclePrice =
  payload.vehiclePriceOverrides?.[vehicleId];

  const { trip, customer } = payload;

  const pickupDateTime =
    `${trip.pickupDate}T${trip.pickupTime}:00`;

  const returnDateTime =
    trip.journeyType === 'round-trip'
      ? `${trip.returnDate}T${trip.returnTime}:00`
      : null;

  const serviceTypes: Record<string, string> = {
    'Day trip': 'daytrips',
    'Day trips': 'daytrips',
    Transfers: 'fleet',
    'Hourly drivers': 'hourly',
    'Hourly Drivers': 'hourly',
  };

  const serviceType = serviceTypes[trip.serviceType];

  if (!serviceType) {
    return {
      success: false,
      message: 'Select a valid service type.',
    };
  }

  try {
    const token = localStorage.getItem(ADMIN_TOKEN_KEY);

    const response = await fetch(
      `${DASHBOARD_API_BASE_URL}/api/admin/bookings/`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token
            ? { Authorization: `Bearer ${token}` }
            : {}),
          ...(API_KEY
            ? { 'X-API-Key': API_KEY }
            : {}),
        },
        body: JSON.stringify({
          service_type: serviceType,
          ...(serviceType === 'daytrips' && trip.dayTripId
            ? { service_id: Number(trip.dayTripId) }
            : {}),
          source_number: trip.sourceNumber,
          source_name: trip.sourceName,
          payment_method: trip.paymentStatus,
          location: trip.pickupLocation,
          pickup_location: trip.pickupLocation,
          drop_location: trip.dropoffLocation,
          pickup_datetime: pickupDateTime,
          return_trip: trip.journeyType === 'round-trip',
          ...(returnDateTime
            ? { return_datetime: returnDateTime }
            : {}),
          passengers: Number(trip.passengers),
          luggage: Number(trip.luggage),
          vehicle: Number(vehicleId),
          ...(bookingVehiclePrice !== undefined &&
          Number.isFinite(bookingVehiclePrice)
            ? { vehicle_price: bookingVehiclePrice }
            : {}),
          traveler_name:
            `${customer.firstName.trim()} ${customer.lastName.trim()}`.trim(),
          traveler_phone: customer.phoneNumber,
          traveler_email: customer.email,
          booking_for_someone_else:
            customer.bookingForSomeoneElse,
          other_person_name:
            customer.otherPersonName,
          other_person_phone:
            customer.otherPersonPhone,
          selected_options: customer.options,
          notes: customer.specialRequirements,
          ...(payload.vehicle_price !== undefined ? { vehicle_price: payload.vehicle_price } : {}),
          ...(payload.final_price !== undefined ? { final_price: payload.final_price, estimated_price: payload.final_price } : {}),
        }),
      }
    );

    const result = await response.json().catch(() => null);

    if (!response.ok) {
      return {
        success: false,
        message:
          result?.message ||
          'Failed to create booking',
        warning: result?.errors
          ? JSON.stringify(result.errors)
          : undefined,
      };
    }

    /*
     * Resolve the vehicle name for the newly created booking too.
     */
    const vehicles = await fetchAdminBookingVehicles();

    const vehicle = vehicles.find(
      (candidate) =>
        String(candidate.id) === String(vehicleId)
    );

    return {
      success: true,
      message: 'Booking created',
      data: mapBookingApiItemToRecord(
        result?.data || result,
        vehicle?.name || vehicle?.category
      ),
    };
  } catch {
    return {
      success: false,
      message: 'Connection failed.',
    };
  }
}

/**
 * Update an existing booking.
 */
export async function updateBooking(
  id: string,
  payload: CreateBookingPayload
): Promise<ApiResponse<BookingRecord>> {
  const selectedVehicles = Object.entries(
    payload.vehicles
  ).filter(
    ([, quantity]) => Number(quantity) > 0
  );

  const [vehicleId] = selectedVehicles[0] || [];

  const bookingVehiclePrice = vehicleId
    ? payload.vehiclePriceOverrides?.[vehicleId]
    : undefined;

  const { trip, customer } = payload;

  const pickupDateTime =
    `${trip.pickupDate}T${trip.pickupTime}:00`;

  const returnDateTime =
    trip.journeyType === 'round-trip'
      ? `${trip.returnDate}T${trip.returnTime}:00`
      : null;

  const locationPayload =
    customer.arrivalType === 'flight' ||
    customer.arrivalType === 'train'
      ? JSON.stringify({
          type: customer.arrivalType,
          company: customer.airline,
          flightOrTrainNumber:
            customer.flightNumber,
          arrivalLocation:
            customer.arrivalTerminal,
          arrivalTime:
            customer.arrivalTime,
        })
      : customer.locationValue?.trim() || '';

  const serviceTypes: Record<string, string> = {
    'Day trip': 'daytrips',
    'Day trips': 'daytrips',
    Transfers: 'fleet',
    'Hourly drivers': 'hourly',
    'Hourly Drivers': 'hourly',
  };

  const serviceType =
    serviceTypes[trip.serviceType];

  if (!serviceType) {
    return {
      success: false,
      message: 'Select a valid service type.',
    };
  }

  try {
    const token =
      localStorage.getItem(ADMIN_TOKEN_KEY);

    const response = await fetch(
      `${DASHBOARD_API_BASE_URL}/api/admin/bookings/${id}/`,
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token
            ? { Authorization: `Bearer ${token}` }
            : {}),
          ...(API_KEY
            ? { 'X-API-Key': API_KEY }
            : {}),
        },
        body: JSON.stringify({
          service_type: serviceType,
          ...(serviceType === 'daytrips' && trip.dayTripId
            ? { service_id: Number(trip.dayTripId) }
            : {}),
          source_number: trip.sourceNumber,
          source_name: trip.sourceName,
          payment_method: trip.paymentStatus,
          location: locationPayload,
          pickup_location:
            trip.pickupLocation,
          drop_location:
            trip.dropoffLocation,
          pickup_datetime: pickupDateTime,
          return_trip:
            trip.journeyType === 'round-trip',
          ...(returnDateTime
            ? { return_datetime: returnDateTime }
            : {}),
          passengers:
            Number(trip.passengers),
          luggage:
            Number(trip.luggage),
          vehicle:
            vehicleId != null
              ? Number(vehicleId)
              : null,

          ...(bookingVehiclePrice !== undefined &&
          Number.isFinite(bookingVehiclePrice)
            ? {
                vehicle_price:
                  bookingVehiclePrice,
                estimated_price:
                  bookingVehiclePrice,
                final_price:
                  bookingVehiclePrice,
              }
            : {}),

          traveler_name:
            `${customer.firstName.trim()} ${customer.lastName.trim()}`.trim(),
          traveler_phone: customer.phoneNumber,
          traveler_email: customer.email,

          booking_for_someone_else:
            customer.bookingForSomeoneElse,
          other_person_name:
            customer.otherPersonName,
          other_person_phone:
            customer.otherPersonPhone,

          selected_options: customer.options,
          notes: customer.specialRequirements,
          ...(payload.vehicle_price !== undefined ? { vehicle_price: payload.vehicle_price } : {}),
          ...(payload.final_price !== undefined ? { final_price: payload.final_price, estimated_price: payload.final_price } : {}),
        }),
      }
    );

    const result =
      await response.json().catch(() => null);

    if (!response.ok) {
      return {
        success: false,
        message:
          result?.message ||
          'Failed to update booking',
        warning: result?.errors
          ? JSON.stringify(result.errors)
          : undefined,
      };
    }

    /*
     * Resolve the vehicle name for the updated booking.
     */
    const vehicles =
      await fetchAdminBookingVehicles();

    const vehicle = vehicles.find(
      (candidate) =>
        String(candidate.id) === String(vehicleId)
    );

    return {
      success: true,
      message:
        result?.message ||
        'Booking updated',
      data: mapBookingApiItemToRecord(
        result?.data || result,
        vehicle?.name || vehicle?.category
      ),
    };
  } catch {
    return {
      success: false,
      message: 'Connection failed.',
    };
  }
}

/**
 * Maps dashboard trip status values to Django Booking.status values.
 */
const tripStatusMapToApi: Record<
  TripStatus,
  string
> = {
  upcoming: 'confirmed',
  'on-trip': 'in_progress',
  completed: 'completed',
  cancelled: 'cancelled',
  confirmed: 'confirmed',
  pending: 'pending',
  incomplete: 'incomplete',
};

/**
 * Update trip status.
 */
export async function updateBookingTripStatus(
  id: string,
  trip: TripStatus
): Promise<ApiResponse> {
  try {
    const token =
      localStorage.getItem(ADMIN_TOKEN_KEY);

    const status =
      tripStatusMapToApi[trip];

    const response = await fetch(
      `${DASHBOARD_API_BASE_URL}/api/admin/bookings/${id}/status/`,
      {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(token
            ? { Authorization: `Bearer ${token}` }
            : {}),
          ...(API_KEY
            ? { 'X-API-Key': API_KEY }
            : {}),
        },
        body: JSON.stringify({ status }),
      }
    );

    const result =
      await response.json().catch(() => null);

    if (!response.ok) {
      return {
        success: false,
        message:
          result?.message ||
          'Failed to update booking status',
      };
    }

    return {
      success: true,
      message:
        result?.message ||
        'Status updated',
    };
  } catch {
    return {
      success: false,
      message: 'Connection failed.',
    };
  }
}

/**
 * Update payment status.
 */
export async function updateBookingPaymentStatus(
  id: string,
  payment: PaymentStatus
): Promise<ApiResponse> {
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/bookings/${id}/payment`,
      {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(API_KEY
            ? { 'X-API-Key': API_KEY }
            : {}),
        },
        body: JSON.stringify({ payment }),
      }
    );

    if (!response.ok) {
      return {
        success: false,
        message: 'Failed to update payment',
      };
    }

    return {
      success: true,
      message: 'Payment updated',
    };
  } catch {
    return {
      success: false,
      message: 'Connection failed.',
    };
  }
}

/**
 * Assign a driver to a booking.
 */
export async function assignDriverToBooking(
  id: string,
  driverId: string,
  driverFare: string,
  meetingBoardUrl?: string,
  includeDriverPhone?: boolean
): Promise<ApiResponse> {
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/bookings/${id}/assign-driver`,
      {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(API_KEY
            ? { 'X-API-Key': API_KEY }
            : {}),
        },
        body: JSON.stringify({
          driverAssigned: true,
          driverId,
          driverFare,
          meetingBoardUrl,
          include_driver_phone: includeDriverPhone ?? false,
        }),
      }
    );

    if (!response.ok) {
      return {
        success: false,
        message: 'Failed to assign driver',
      };
    }

    return {
      success: true,
      message: 'Driver assigned',
    };
  } catch {
    return {
      success: false,
      message: 'Connection failed.',
    };
  }
}

export async function removeDriverFromBooking(
  id: string
): Promise<ApiResponse> {
  try {
    const token =
      localStorage.getItem(ADMIN_TOKEN_KEY);

    const response = await fetch(
      `${DASHBOARD_API_BASE_URL}/api/admin/bookings/${id}/remove-driver/`,
      {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...(token
            ? { Authorization: `Bearer ${token}` }
            : {}),
          ...(API_KEY
            ? { 'X-API-Key': API_KEY }
            : {}),
        },
      }
    );

    const result =
      await response.json().catch(() => null);

    if (!response.ok) {
      return {
        success: false,
        message:
          result?.message ||
          'Failed to remove driver',
      };
    }

    return {
      success: true,
      message:
        result?.message ||
        'Driver removed successfully',
    };
  } catch {
    return {
      success: false,
      message: 'Connection failed.',
    };
  }
}

/**
 * Cancel/Delete booking.
 */
export async function removeBookingFromBackend(
  id: string
): Promise<ApiResponse> {
  try {
    const token =
      localStorage.getItem(ADMIN_TOKEN_KEY);

    const response = await fetch(
      `${DASHBOARD_API_BASE_URL}/api/admin/bookings/${id}/remove/`,
      {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(token
            ? { Authorization: `Bearer ${token}` }
            : {}),
          ...(API_KEY
            ? { 'X-API-Key': API_KEY }
            : {}),
        },
      }
    );

    const result =
      await response.json().catch(() => null);

    if (!response.ok) {
      return {
        success: false,
        message:
          result?.message ||
          'Failed to remove booking',
      };
    }

    return {
      success: true,
      message:
        result?.message ||
        'Booking removed successfully',
    };
  } catch {
    return {
      success: false,
      message: 'Connection failed.',
    };
  }
}

export const notifyCustomer = async (
  id: string,
  subject: string,
  message: string
): Promise<ApiResponse> => {
  try {
    const token =
      localStorage.getItem(ADMIN_TOKEN_KEY);

    const response = await fetch(
      `${DASHBOARD_API_BASE_URL}/api/admin/bookings/${id}/notify/`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token
            ? { Authorization: `Bearer ${token}` }
            : {}),
          ...(API_KEY
            ? { 'X-API-Key': API_KEY }
            : {}),
        },
        body: JSON.stringify({
          subject,
          message,
        }),
      }
    );

    const result =
      await response.json().catch(() => null);

    if (!response.ok) {
      return {
        success: false,
        message:
          result?.message ||
          'Failed to send notification.',
      };
    }

    return {
      success: true,
      message:
        result?.message ||
        'Notification sent successfully.',
      data: result?.data,
    };
  } catch {
    return {
      success: false,
      message: 'Connection failed.',
    };
  }
};